import React, { useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setAiReport } from 'store/actions/appActions';
import axiosInstance from 'utils/axiosUtil';
import { CLAIMS_RANGE_MSG } from 'utils/claimsRange';
import routeUpdateState from '../../components/chatbot/updateStateRouter';
import { ChatBotContext } from '../ChatBotContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { GetApp, SearchOff } from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { Chart as GeoChart } from 'react-google-charts';
import { ReportCountyMap } from './ReportCountyMap';
import { ClaimsDateProvider } from '../../components/ClaimsDateProvider';
import { DynamicTable } from 'components';
import fileDownload from 'js-file-download';
import { ColorUtil } from 'utils';
import { CsvUtil } from 'doctivity-shared/utils';

/**
 * AI Custom Reporting — report page.
 *
 * Reads the report payload Key sends via `launch_report` (Redux state.app.aiReport)
 * and renders it as a titled chart + table. CSV export, narrative, and save/schedule
 * are layered on next — this is the "page refines" surface the chat hands off to.
 */

const CHART_TYPES = new Set(['bar', 'line', 'pie']);
const MAX_CATEGORIES = 25; // cap so a big ranked report stays readable as a chart

// Generic ApexCharts palette for non-semantic series.
const DEFAULT_COLORS = ['#008FFB', '#00E396', '#FEB019', '#775DD0', '#3F51B5', '#03A9F4'];

// Secondary-axis overlay line (e.g. "% Leaked") — a dark amber that reads against the
// blue/red in-network / out-of-network bars.
const RATE_COLOR = '#B45309';

// Canonical affiliated/non-affiliated colors (match the rest of the app's claims charts).
// Non-affiliated / out-of-network → red; affiliated / in-network → blue. Check the
// "non"/"out" forms FIRST since "non_affiliated" also contains "affiliated".
const semanticColor = (key, label) => {
  const s = `${key} ${label}`.toLowerCase();
  if (/non[\s_-]?affiliated/.test(s) || /out[\s_-]?of[\s_-]?network/.test(s)) return ColorUtil.NonAffiliatedColor;
  if (/affiliated/.test(s) || /in[\s_-]?network/.test(s)) return ColorUtil.AffiliatedColor;
  return null;
};

const compact = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return Math.abs(n) >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : n;
};

// Full currency for cells + tooltips: $3,528,581
const formatCurrency = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : String(v ?? '');
};

// Compact currency for chart axis labels: $3.5M / $720K
const compactCurrency = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  const a = Math.abs(n);
  if (a >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

/**
 * Build an ApexCharts config from the report's visualization spec + shaped rows.
 * Generic: every numeric column becomes a series (so e.g. affiliated vs non-affiliated
 * render as grouped bars), the category axis is visualization.x or the first
 * non-numeric column. Returns null when a chart doesn't apply (type 'table', no data).
 */
function buildChart(visualization, cols, rows) {
  const type = visualization?.type;
  if (!CHART_TYPES.has(type) || !rows.length || !cols.length) return null;

  const isNumericCol = (key) => rows.some((r) => typeof r[key] === 'number');
  const numericKeys = cols.map((c) => c.key).filter(isNumericCol);
  if (!numericKeys.length) return null;

  const xKey = (visualization.x && cols.some((c) => c.key === visualization.x))
    ? visualization.x
    : (cols.find((c) => !numericKeys.includes(c.key)) || {}).key;
  if (!xKey) return null;

  const data = rows.slice(0, MAX_CATEGORIES);
  const categories = data.map((r) => String(r[xKey] ?? ''));
  const labelFor = (key) => (cols.find((c) => c.key === key) || {}).label || key;

  const isCurrency = (key) => ((cols.find((c) => c.key === key) || {}).format === 'currency');

  if (type === 'pie') {
    // Single-row, multi-metric total → one slice per numeric COLUMN (e.g. affiliated vs
    // non-affiliated). Otherwise the usual pie: one slice per ROW by a single metric.
    const valueKeys = numericKeys.filter((k) => k !== xKey);
    if (data.length === 1 && valueKeys.length >= 2) {
      const row = data[0];
      const tip = valueKeys.every(isCurrency) ? formatCurrency : compact;
      let pal = 0;
      return {
        type: 'pie',
        height: 380,
        series: valueKeys.map((k) => Number(row[k]) || 0),
        options: {
          labels: valueKeys.map((k) => labelFor(k)),
          colors: valueKeys.map((k) => semanticColor(k, labelFor(k)) || DEFAULT_COLORS[pal++ % DEFAULT_COLORS.length]),
          legend: { position: 'bottom' },
          tooltip: { y: { formatter: tip } },
        },
      };
    }
    const yKey = (visualization.y && numericKeys.includes(visualization.y)) ? visualization.y : numericKeys[0];
    const tip = isCurrency(yKey) ? formatCurrency : compact;
    return {
      type: 'pie',
      height: 380,
      series: data.map((r) => Number(r[yKey]) || 0),
      options: {
        labels: categories,
        legend: { position: 'bottom' },
        tooltip: { y: { formatter: tip } },
      },
    };
  }

  // Percent columns (e.g. "% Leaked") are a different scale — keep them out of the
  // value-axis series; they stay in the table.
  const colFormat = (k) => (cols.find((c) => c.key === k) || {}).format;
  let seriesKeys = numericKeys.filter((k) => k !== xKey && colFormat(k) !== 'percent');
  // A recipe can pin the chart series explicitly (e.g. plot shared-patient VOLUME but not
  // the much-smaller provider COUNT alongside it, which would render as invisible bars).
  // When set, it wins over the "every numeric column" default.
  if (Array.isArray(visualization.series) && visualization.series.length) {
    const allow = new Set(visualization.series);
    seriesKeys = seriesKeys.filter((k) => allow.has(k));
  }
  const allCurrency = seriesKeys.length > 0 && seriesKeys.every(isCurrency);
  const axisFmt = allCurrency ? compactCurrency : compact;
  const tipFmt = allCurrency ? formatCurrency : compact;
  // Affiliated → blue, Non-affiliated → red; other series fall back to the palette.
  let palette = 0;
  const colors = seriesKeys.map((k) => semanticColor(k, labelFor(k)) || DEFAULT_COLORS[palette++ % DEFAULT_COLORS.length]);

  // Optional secondary-axis overlay: a recipe can plot e.g. "% Leaked" as a line on a
  // right-hand axis (its own scale) over the bars, instead of being crushed onto the
  // count axis. rightAxisSeries lists those column keys (percent columns allowed here).
  const rightKeys = (Array.isArray(visualization.rightAxisSeries) ? visualization.rightAxisSeries : [])
    .filter((k) => cols.some((c) => c.key === k) && !seriesKeys.includes(k));
  if (rightKeys.length) {
    const allRightPct = rightKeys.every((k) => colFormat(k) === 'percent');
    const rightFmt = allRightPct ? (v) => `${Math.round(v)}%` : compact;
    const barSeries = seriesKeys.map((k) => ({ name: labelFor(k), type: 'column', data: data.map((r) => Number(r[k]) || 0) }));
    const lineSeries = rightKeys.map((k) => ({ name: labelFor(k), type: 'line', data: data.map((r) => Number(r[k]) || 0) }));
    const barNames = barSeries.map((s) => s.name);
    const lineNames = lineSeries.map((s) => s.name);
    // EVERY series must map to a yaxis (via seriesName) or ApexCharts throws building its
    // series→axis map. Bind all bars to ONE shared left axis (seriesName array), all
    // overlay lines to one right axis.
    const yaxis = [{ seriesName: barNames, labels: { formatter: axisFmt }, title: { text: 'Shared Patients' } }];
    if (barNames.length === 0) yaxis.pop();
    yaxis.push({
      seriesName: lineNames,
      opposite: true,
      min: 0,
      max: allRightPct ? 100 : undefined,
      labels: { formatter: rightFmt },
      title: { text: lineNames.length === 1 ? lineNames[0] : 'Rate' },
    });
    const orderedKeys = [...seriesKeys, ...rightKeys];
    return {
      type: 'line', // mixed bar + line
      height: 380,
      series: [...barSeries, ...lineSeries],
      options: {
        colors: [...colors, ...rightKeys.map(() => RATE_COLOR)],
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '65%' } },
        stroke: { width: [...barSeries.map(() => 0), ...lineSeries.map(() => 3)], curve: 'straight' },
        markers: { size: 4 },
        dataLabels: { enabled: false },
        xaxis: { categories, labels: { rotate: -45, hideOverlappingLabels: true, trim: true } },
        yaxis,
        legend: { position: 'top' },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, opts) => {
              const k = orderedKeys[opts?.seriesIndex];
              return (k && colFormat(k) === 'percent') ? rightFmt(val) : tipFmt(val);
            },
          },
        },
      },
    };
  }

  return {
    type, // 'bar' | 'line'
    height: 380,
    series: seriesKeys.map((k) => ({ name: labelFor(k), data: data.map((r) => Number(r[k]) || 0) })),
    options: {
      colors,
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: '65%' } },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { rotate: -45, hideOverlappingLabels: true, trim: true } },
      yaxis: { labels: { formatter: axisFmt } },
      legend: { position: 'top' },
      tooltip: { y: { formatter: tipFmt } },
    },
  };
}

/**
 * Build a Google GeoChart (US-state heat) config from a 'map' visualization spec.
 * Aggregates the (county) rows up to states by valueKey; states use 'US-XX' codes.
 * Returns null unless visualization.type === 'map'.
 */
function buildMap(visualization, rows) {
  if (visualization?.type !== 'map' || !rows.length) return null;
  const stateKey = visualization.stateKey || 'state';
  const vKey = visualization.valueKey || Object.keys(rows[0]).find((k) => typeof rows[0][k] === 'number');
  if (!vKey) return null;

  const byState = {};
  rows.forEach((r) => {
    const st = String(r[stateKey] || '').trim().toUpperCase();
    if (st.length === 2) byState[st] = (byState[st] || 0) + (Number(r[vKey]) || 0);
  });
  if (!Object.keys(byState).length) return null;

  const data = [['State', 'Volume'], ...Object.entries(byState).map(([st, v]) => [`US-${st}`, v])];
  return {
    data,
    options: {
      region: 'US',
      resolution: 'provinces',
      colorAxis: { colors: ['#E8F5F1', '#00A887'] },
      datalessRegionColor: '#F5F5F5',
      backgroundColor: 'transparent',
    },
  };
}

// CsvUtil.matrixToString joins on commas with no escaping, so escape cells here.
const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const isScalar = (v) => v == null || ['string', 'number', 'boolean'].includes(typeof v);
const RIGHT_ALIGNED = new Set(['currency', 'number', 'percent']);
const formatValue = (v, format) => {
  if (v == null) return '';
  if (format === 'currency') return formatCurrency(v);
  if (format === 'number') {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString() : String(v);
  }
  if (format === 'percent') {
    const n = Number(v);
    return Number.isFinite(n) ? `${n.toLocaleString()}%` : String(v);
  }
  if (isScalar(v)) return String(v);
  try { return JSON.stringify(v); } catch { return ''; }
};

// Prefer backend-provided columns ([{ key/label/format }] or string[]); else derive from rows.
function resolveColumns(columns, rows) {
  if (Array.isArray(columns) && columns.length) {
    return columns.map((c) =>
      (typeof c === 'string'
        ? { key: c, label: c }
        : { key: c.key ?? c.field ?? c.label, label: c.label ?? c.key ?? c.field, format: c.format }));
  }
  if (rows && rows.length) {
    return Object.keys(rows[0])
      .filter((k) => isScalar(rows[0][k]))
      .map((k) => ({ key: k, label: k }));
  }
  return [];
}

// Numeric-aware comparison for client-side sorting of the in-memory report rows.
function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

// Map report columns ({ key, label, format }) to DynamicTable columns: reuse formatValue
// for commas / $ / %, right-align numerics, keep them sortable, no per-column filters.
function toDynamicColumns(cols) {
  return (cols || []).map((c) => {
    const rightAlign = RIGHT_ALIGNED.has(c.format);
    return {
      key: c.key,
      label: c.label,
      sortable: true,
      filter: false,
      // DynamicTable body cells DON'T inherit column.style (only the header does), so a
      // numeric value must be right-aligned by wrapping it — the app-wide convention (see
      // OrganizationsPage). column.style still right-aligns the HEADER so the label lines up
      // over the number. Without the wrapper the header sits right and the value sits left.
      format: rightAlign
        ? (v) => <div style={{ textAlign: 'right' }}>{formatValue(v, c.format)}</div>
        : (v) => formatValue(v, c.format),
      style: rightAlign ? { textAlign: 'right' } : undefined,
    };
  });
}

// Fill the react-chatbot-kit input the React-correct way (controlled field: native setter
// + input event) and click Send. Returns false if the chat isn't mounted yet.
function fillChatInput(prompt) {
  const input = document.querySelector('.react-chatbot-kit-chat-input');
  const btn = document.querySelector('.react-chatbot-kit-chat-btn-send');
  if (!input || !btn) return false;
  const proto = input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement : window.HTMLInputElement;
  Object.getOwnPropertyDescriptor(proto.prototype, 'value').set.call(input, prompt);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  setTimeout(() => btn.click(), 60);
  return true;
}

// Poll for the chat input (while the panel opens) then fill + send.
function submitViaChat(prompt) {
  let tries = 0;
  const attempt = () => { if (!fillChatInput(prompt) && tries < 25) { tries += 1; setTimeout(attempt, 120); } };
  setTimeout(attempt, 200);
}

// Headless: POST the prompt to Key (same endpoint the chat uses). If it produced a report,
// route it so it renders WITHOUT opening the chat, and return true. Otherwise return false
// so the caller can open the chat for the conversation (Key needs more info).
async function askKeyHeadless({ prompt, user, clientId, dispatch }) {
  const resp = await axiosInstance.post('/AI', {
    question: prompt,
    user,
    messages: [{ role: 'developer', content: CLAIMS_RANGE_MSG }],
    url: window.location.pathname,
    previous_response_id: null,
    prompt: sessionStorage.getItem('system_prompt'),
    client_id: clientId,
    audio_mode: false,
  });
  const update = resp?.data?.update_state;
  if (update && update.includes('launch_report:')) {
    routeUpdateState(update, dispatch, clientId, { navigate: () => {} });
    return true;
  }
  return false;
}

// Click an example: if the chat is already open (text or voice), submit there. Otherwise run
// headless and only open the chat if Key needs more info (or the call fails).
async function askKey({ prompt, user, clientId, dispatch, openChat }) {
  if (document.querySelector('.react-chatbot-kit-chat-input')) { // chat already open
    fillChatInput(prompt);
    return;
  }
  let handled = false;
  try { handled = await askKeyHeadless({ prompt, user, clientId, dispatch }); } catch (e) { handled = false; }
  if (!handled) { // needs more info or errored — fall back to the chat
    if (typeof openChat === 'function') openChat();
    submitViaChat(prompt);
  }
}

// Example prompts for the landing — click to ask Key. Curated from the recipe catalog.
const EXAMPLE_QUERIES = [
  { group: 'Shared patients & specialties',
    items: [
      'Show total shared patients for my affiliated providers, grouped by specialty',
      'Which specialty pairs share the most patients?',
      'How many patients do two specific providers share, by service line?',
    ] },
  { group: 'Leakage & reimbursement',
    items: [
      'Which specialties are leaking the most patients out of network?',
      'Rank service lines by Medicare reimbursement value, affiliated vs non-affiliated',
      'Show me referral leakage opportunities for my group',
    ] },
  { group: 'Providers & geography',
    items: [
      'Which non-affiliated providers share the most patients with my providers?',
      'Where do inbound patients come from, by county?',
      'Top 10 providers by patients in Pennsylvania',
    ] },
];

export default function ReportBuilderPage() {
  const report = useSelector((state) => state.app?.aiReport) || {};
  const user = useSelector((state) => state.user) || {};
  const selectedClient = useSelector((state) => state.app?.selectedClient);
  const { openChat } = useContext(ChatBotContext);
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false); // running an example query headlessly
  const clearReport = () => dispatch(setAiReport(null)); // reset to the landing
  const runExample = async (q) => {
    setBusy(true);
    try { await askKey({ prompt: q, user, clientId: selectedClient, dispatch, openChat }); } finally { setBusy(false); }
  };
  const { title, reportType, visualization, columns, rows = [], count = 0 } = report;

  const cols = resolveColumns(columns, rows);
  const chart = buildChart(visualization, cols, rows);
  // County-level map when the recipe supplies a county FIPS (geoIdKey) and rows carry it;
  // otherwise fall back to the state-level GeoChart.
  const countyMap = visualization?.type === 'map' && visualization.geoIdKey
    && rows.some((r) => r[visualization.geoIdKey]);
  const map = !countyMap && buildMap(visualization, rows);
  const hasReport = rows.length > 0 || !!reportType;

  // DynamicTable pages/sorts server-side by design, but a report's rows are already fully
  // in memory — so we sort + slice them here client-side and hand it one page + the total.
  const [query, setQuery] = useState({ limit: 25, offset: 0, order: null });
  const reportKey = `${title}|${reportType}|${count}|${rows.length}`;
  const [lastKey, setLastKey] = useState(reportKey);
  if (reportKey !== lastKey) { // a new report arrived — reset paging/sort, keep page size
    setLastKey(reportKey);
    setQuery((q) => ({ limit: q.limit, offset: 0, order: null }));
  }
  const dynColumns = toDynamicColumns(cols);
  let orderedRows = rows;
  const ord = Array.isArray(query.order) && Array.isArray(query.order[0]) ? query.order[0] : null;
  if (ord) {
    const dir = String(ord[ord.length - 1]).toUpperCase() === 'DESC' ? -1 : 1;
    const key = ord.slice(0, -1).join('.');
    orderedRows = [...rows].sort((a, b) => compareValues(a[key], b[key]) * dir);
  }
  const pageRows = orderedRows
    .slice(query.offset, query.offset + query.limit)
    .map((r, i) => ({ ...r, __rowid: query.offset + i })); // stable key for DynamicTable

  // Client-side CSV export — the report rows/columns are already in the page.
  // Export raw values (not the $-formatted display) so the numbers are usable in Excel.
  const exportCsv = () => {
    const matrix = [
      cols.map((c) => csvEscape(c.label)),
      ...rows.map((r) => cols.map((c) => csvEscape(r[c.key]))),
    ];
    const name = `${String(title || reportType || 'report').replace(/[^\w]+/g, '_').slice(0, 60)}.csv`;
    fileDownload(CsvUtil.matrixToString(matrix), name);
  };

  // Dark page header bar — same metrics as other pages (padding spacing(3) /
  // paddingBottom 0, title 24px on theme.palette.primary.accent) so the page is the
  // standard height and the fixed top-right chat buttons stay visible against it.
  const header = (
    <Box sx={{
      bgcolor: (t) => t.palette.primary.accent,
      minHeight: 111, // align the bar's bottom with the nav's dark-green logo section
      px: 3,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    }}
    >
      <Typography sx={{ color: '#FFFFFF', fontSize: 24, pr: '30px' }}>Advanced AI Reporting</Typography>
    </Box>
  );

  // Admin-gated in the component (not the route) so a direct /reports URL always renders.
  const userLoaded = !!(user.id || user.meta?.authenticated);
  if (userLoaded && !user.is_admin) {
    return (
      <Box>
        {header}
        <Box sx={{ p: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
              <Typography variant='h6' gutterBottom>Admins only</Typography>
              <Typography variant='body2'>Advanced AI Reporting is available to administrators.</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!hasReport) {
    // Landing / stub — click an example to open Key and run it.
    return (
      <Box>
        {header}
        <Box sx={{ p: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant='h6' sx={{ flex: 1 }}>Ask Key for a report</Typography>
                <ClaimsDateProvider />
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                Describe what you want in the chat, or click an example below to run it.
              </Typography>
              {busy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
                  <CircularProgress size={16} />
                  <Typography variant='body2'>Asking Key…</Typography>
                </Box>
              )}
              {EXAMPLE_QUERIES.map((grp) => (
                <Box key={grp.group} sx={{ mb: 2.5 }}>
                  <Typography variant='overline' color='text.secondary'>{grp.group}</Typography>
                  <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
                    {grp.items.map((q) => (
                      <Chip
                        key={q}
                        label={q}
                        variant='outlined'
                        clickable
                        disabled={busy}
                        onClick={() => runExample(q)}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {header}
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {title && <Typography variant='h6'>{title}</Typography>}
              <IconButton onClick={exportCsv} disabled={!rows.length} size='large' title='Export CSV'>
                <GetApp sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton onClick={clearReport} size='large' title='Clear report (back to examples)'>
                <SearchOff sx={{ fontSize: 20 }} />
              </IconButton>
              {/* Claims date range pushed to the far right (upper-right) — same source as Market Data. */}
              <Box sx={{ ml: 'auto', mt: '4px' }}><ClaimsDateProvider /></Box>
            </Box>

            <Stack direction='row' spacing={1} sx={{ mb: 2 }} flexWrap='wrap'>
              <Chip size='small' variant='outlined' label={`${count} rows`} />
            </Stack>

            {countyMap && (
              <Box sx={{ mb: 2 }}>
                <ReportCountyMap
                  rows={rows}
                  valueKey={visualization.valueKey}
                  geoIdKey={visualization.geoIdKey}
                  countyKey={visualization.countyKey || 'county'}
                  stateKey={visualization.stateKey || 'state'}
                  valueLabel={(cols.find((c) => c.key === visualization.valueKey) || {}).label}
                />
                <Typography variant='caption' color='text.secondary'>
                  County-level heat; full detail in the table below.
                </Typography>
              </Box>
            )}

            {map && (
              <Box sx={{ mb: 2 }}>
                <GeoChart chartType='GeoChart' data={map.data} options={map.options} width='100%' height='360px' />
                <Typography variant='caption' color='text.secondary'>
                  Heat by state; county detail in the table below.
                </Typography>
              </Box>
            )}

            {chart && (
              <Box sx={{ mb: 2 }}>
                <Chart options={chart.options} series={chart.series} type={chart.type} height={chart.height} />
                {rows.length > MAX_CATEGORIES && (
                  <Typography variant='caption' color='text.secondary'>
                    Chart shows the top {MAX_CATEGORIES} of {rows.length} rows; full data in the table below.
                  </Typography>
                )}
              </Box>
            )}

            <DynamicTable
              columns={dynColumns}
              data={{ rows: pageRows, count: rows.length }}
              query={query}
              onQueryChange={setQuery}
              showFilters={false}
              noDataMessage='No rows.'
              idKey='__rowid'
              ContainerType={React.Fragment}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
